import {
  mixpanelUtils,
  runOnce,
  cookiesUtils,
  getGtmIdentifiersAttributes,
} from '../../utils';
import { IUser } from '../../models';
import { ANALYTICS } from '../../constants/analytics/analytics';
import { useSelector } from 'react-redux';
import { selectAnalyticsSuperAttributes } from '../../store/selectors';
import { RootState } from '../../store/store';
import { COOKIE_KEYS } from '../../constants/storage/cookies';
import { STORAGE_KEYS } from '../../constants/storage/storage';

/**
 * Hook to use Mixpanel throughout the application
 * @returns Object with Mixpanel tracking functions
 */
export const useMixpanel = () => {
  const user = useSelector((state: RootState) => state.user);
  const superAttributes = useSelector(selectAnalyticsSuperAttributes);

  /**
   * Fire First App Open event if the cookie was set by the extension
   * This event should only be sent once per lifetime of the app
   */
  const fireFirstAppOpenIfNeeded = (): void => {
    if (cookiesUtils.getBooleanCookie(COOKIE_KEYS.FIRST_APP_OPEN)) {
      cookiesUtils.removeCookie(COOKIE_KEYS.FIRST_APP_OPEN);
      runOnce(STORAGE_KEYS.FIRST_APP_OPEN_SENT, () => {
        track(ANALYTICS.EVENTS.FIRST_APP_OPEN);
      });
    }
  };

  /**
   * init mixpanel
   * @param user
   */
  const init = async (user: IUser) => {
    await mixpanelUtils.init(user);
    fireFirstAppOpenIfNeeded();
  };

  /**
   * update super attributes according to state
   * if user is not premium, unregister relevant keys
   */
  const updateSuperAttributes = () => {
    if (!user.isPremium) {
      mixpanelUtils.unregister(ANALYTICS.PROPERTIES.SUBSCRIPTION_STATUS);
      mixpanelUtils.unregister(ANALYTICS.PROPERTIES.SUBSCRIPTION_IS_ON_TRIAL);
      mixpanelUtils.unregister(ANALYTICS.PROPERTIES.SUBSCRIPTION_PLAN);
      mixpanelUtils.unregister(ANALYTICS.PROPERTIES.SUBSCRIPTION_AGE);
    }
    // Don't keep a stale App Version when the extension version is unknown.
    if (!superAttributes[ANALYTICS.PROPERTIES.APP_VERSION]) {
      mixpanelUtils.unregister(ANALYTICS.PROPERTIES.APP_VERSION);
    }
    mixpanelUtils.register({
      ...superAttributes,
      ...getGtmIdentifiersAttributes(),
    });
  };

  /**
   * Track an event in Mixpanel
   * @param eventName - Name of the event to track
   * @param properties - Optional event properties
   */
  const track = (eventName: string, properties?: Record<string, any>) => {
    updateSuperAttributes();
    mixpanelUtils.track(eventName, properties);
  };

  /**
   * Identify a user in Mixpanel and set profile properties
   * @param userId - The unique user identifier
   * @param email - User's email (optional, for setting profile)
   */
  const identify = (userId: string, email?: string) => {
    mixpanelUtils.identify(userId);
    if (email) {
      mixpanelUtils.setUserProfileOnLogin(email, userId);
    }
  };

  /**
   * Update user profile on logout
   * Note: We intentionally don't call mixpanel.reset() to keep the same device_id
   * This means multiple users on the same device will be linked together in Mixpanel
   */
  const onLogout = () => {
    mixpanelUtils.setUserProfileOnLogout();
  };

  return {
    track,
    identify,
    onLogout,
    init,
  };
};
